import React, { useState, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import 'pages/ShareYourLight/components/star-twinkle.css';
import { Box } from '@mui/material';
import styled from '@emotion/styled';
import ThoughtViewModal from 'pages/ShareYourLight/components/ThoughtViewModal';
import Lantern, { LanternColor, LanternSize } from 'pages/ShareYourLight/components/Lantern';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import SYLButton from 'pages/ShareYourLight/components/SYLButton';
// Styled div for animated lantern
const LanternFloatDiv = styled.div<{ customStyle: React.CSSProperties }>(
  ({ customStyle }) => ({ ...customStyle }),
);


interface LanternSkyProps {
  thoughts: string[];
  setAddMode: React.Dispatch<React.SetStateAction<boolean>>;
  likedLanterns: { [thoughtId: string]: boolean };
  setLikedLanterns: React.Dispatch<React.SetStateAction<{ [thoughtId: string]: boolean }>>;
  openThoughtId: string;
  setOpenThoughtId: React.Dispatch<React.SetStateAction<string>>;
  csrfToken: string;
  refetchCsrfToken: () => Promise<string | undefined>;
}

function LanternSky({
  thoughts,
  setAddMode,
  likedLanterns,
  setLikedLanterns,
  openThoughtId,
  setOpenThoughtId,
  csrfToken,
  refetchCsrfToken,
}: LanternSkyProps) {
  // Store float animation values for each lantern
  const floatAnimRef = React.useRef<{ duration: number; delay: number }[]>([]);

  const [selected, setSelected] = useState<string>('');
  const [lanternPositions, setLanternPositions] = useState<{ left: string; top: string }[]>([]);
  const [lanternAttributes, setLanternAttributes] = useState<{
    color: LanternColor;
    size: LanternSize;
    rotationSeed: number;
  }[]>([]);
  const [lanternsPerRow, setLanternsPerRow] = useState(7);
  // Local state for lantern color and lit status
  const [lanternState, setLanternState] = useState<{
    [id: string]: { color: LanternColor; unlit: boolean };
  }>({});

  // Generate float animation values only when thoughts.length changes
  useEffect(() => {
    const arr: { duration: number; delay: number }[] = [];
    for (let i = 0; i < thoughts.length; i += 1) {
      arr.push({
        duration: 4 + Math.random() * 2, // 4s to 6s
        delay: Math.random() * 1, // 0-1s
      });
    }
    floatAnimRef.current = arr;
  }, [thoughts.length]);

  // Responsive breakpoints: >=1200px: 7, >=900px: 5, >=600px: 4, <600px: 2
  useEffect(() => {
    function getLanternsPerRow() {
      const w = window.innerWidth;
      if (w >= 1200) return 7;
      if (w >= 900) return 5;
      if (w >= 600) return 4;
      return 3;
    }
    let lastBreakpoint = getLanternsPerRow();
    setLanternsPerRow(lastBreakpoint);
    function handleResize() {
      const current = getLanternsPerRow();
      if (current !== lastBreakpoint) {
        setLanternsPerRow(current);
        lastBreakpoint = current;
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Assume all lanterns are big for spacing
    const lanternWidth = 74.02;
    const lanternMargin = 16;
    const rowHeight = 180;
    const colWidth = 100 / lanternsPerRow;
    const positions: { left: string; top: string }[] = [];
    const attributes: { color: LanternColor; size: LanternSize; rotationSeed: number }[] = [];
    const colors: LanternColor[] = ['yellow', 'red', 'blue'];
    const lanternSizes: LanternSize[] = ['sm', 'md', 'lg'];
    let row = 0;
    let col = 0;
    for (let idx = 0; idx < thoughts.length; idx += 1) {
      // If lantern would overflow, move to next row
      if ((col + 1) * (lanternWidth + lanternMargin) > window.innerWidth) {
        row += 1;
        col = 0;
      }
      const jitterX = (Math.random() - 0.5) * 8;
      const jitterY = (Math.random() - 0.5) * rowHeight * 0.2;
      let left = col * colWidth + colWidth / 5 + jitterX;
      left = Math.max(5, Math.min(95, left));
      const top = row * rowHeight + rowHeight / 2 + jitterY;
      positions.push({
        left: `${left}%`,
        top: `${top}px`,
      });
      // Assign random attributes for this lantern
      attributes.push({
        color: colors[Math.floor(Math.random() * colors.length)],
        size: lanternSizes[Math.floor(Math.random() * lanternSizes.length)],
        rotationSeed: Math.random() < 0.5 ? 1 : -1,
      });
      col += 1;
      if (col >= lanternsPerRow) {
        row += 1;
        col = 0;
      }
    }
    setLanternPositions(positions);
    setLanternAttributes(attributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thoughts.length, lanternsPerRow]);

  const renderStars = React.useMemo(() => {
    const stars = [];
    const placedStars: { x: number; y: number; effectiveRadius: number }[] = [];
    const totalStars = 500;
    const skyWidth = window.innerWidth;
    const skyHeight = window.innerHeight;

    // Grid size for spatial partitioning
    const gridSize = 80; // px, adjust for performance/accuracy
    const gridCols = Math.ceil(skyWidth / gridSize);
    const gridRows = Math.ceil(skyHeight / gridSize);
    // Each cell contains indices of placedStars
    const grid: number[][][] = Array.from({
      length: gridCols,
    }, () => Array.from({ length: gridRows }, () => []));

    const getGridCells = (x: number, y: number, radius: number) => {
      const minCol = Math.max(0, Math.floor((x - radius) / gridSize));
      const maxCol = Math.min(gridCols - 1, Math.floor((x + radius) / gridSize));
      const minRow = Math.max(0, Math.floor((y - radius) / gridSize));
      const maxRow = Math.min(gridRows - 1, Math.floor((y + radius) / gridSize));
      const cells: [number, number][] = [];
      for (let col = minCol; col <= maxCol; col += 1) {
        for (let row = minRow; row <= maxRow; row += 1) {
          cells.push([col, row]);
        }
      }
      return cells;
    };

    const checkOverlap = (x: number, y: number, radius: number) => {
      const cells = getGridCells(x, y, radius);
      for (const [col, row] of cells) {
        for (const idx of grid[col][row]) {
          const star = placedStars[idx];
          const dx = x - star.x;
          const dy = y - star.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < radius + star.effectiveRadius) {
            return true;
          }
        }
      }
      return false;
    };

    let i = 0;
    let attempts = 0;
    const maxAttempts = totalStars * 20;

    while (i < totalStars && attempts < maxAttempts) {
      attempts += 1;
      // Only single stars, no clusters
      // Size distribution: more large stars, and some even larger
      let size;
      const r = Math.random();
      if (r < 0.8) {
        // 80% large
        size = 14 + Math.random() * 18;
      }
      else if (r < 0.9 && r > 0.8) {
        // 10% medium
        size = 4 + Math.random() * 8;
      }
      else {
        // 20% small
        size = 1 + Math.random() * 1.5;
      }

      let effectiveRadius = size;
      // Reduce the extra spacing for large stars to allow more clustering
      if (size > 18) effectiveRadius += 36; // was 64
      else if (size > 8) effectiveRadius += 18; // was 32
      else if (size > 3) effectiveRadius += 8; // was 16
      else effectiveRadius += 2;

      // Stronger edge margin for all stars, especially large ones
      // Small: 4%, medium: 7%, large: 12%, very large: 16%
      let edgeMargin = 4;
      if (size > 18) edgeMargin = 16;
      else if (size > 12) edgeMargin = 12;
      else if (size > 6) edgeMargin = 7;
      const minXPercent = edgeMargin;
      const maxXPercent = 100 - edgeMargin;

      const xPercent = Math.random() * (maxXPercent - minXPercent) + minXPercent;
      // Use a more even Y distribution (not squared) and allow stars to cover the full height
      const yPercent = Math.random() * 100;

      const x = (xPercent / 100) * skyWidth;
      const y = (yPercent / 100) * skyHeight;

      if (!checkOverlap(x, y, effectiveRadius)) {
        placedStars.push({ x, y, effectiveRadius });
        // Add to grid
        const starIdx = placedStars.length - 1;
        for (const [col, row] of getGridCells(x, y, effectiveRadius)) {
          grid[col][row].push(starIdx);
        }

        let starOpacity = 0.5;
        if (yPercent > 60) {
          starOpacity = 0.5 * Math.max(0, 1 - (yPercent - 60) / 20); // Fade out
        }

        // Make red stars much less frequent so yellow stars dominate
        const isRedStar = (size > 12 && Math.random() < 0.18) || (size > 6 && Math.random() < 0.05);
        const gradId = isRedStar ? `star-red-gradient-${i}` : `star-gradient-${i}`;
        let filter = 'drop-shadow(0 0 6px 2px #FFC51888)';
        if (size > 18) {
          filter = isRedStar
            ? 'drop-shadow(0 0 64px 32px #FF7A1888) drop-shadow(0 0 128px 64px #FF7A1844)'
            : 'drop-shadow(0 0 48px 24px #FFC51888) drop-shadow(0 0 96px 48px #FFC51844)';
        }
        else if (size > 8) {
          filter = isRedStar
            ? 'drop-shadow(0 0 32px 16px #FF7A1888) drop-shadow(0 0 64px 32px #FF7A1844)'
            : 'drop-shadow(0 0 32px 16px #FFC51888) drop-shadow(0 0 64px 32px #FFC51844)';
        }
        else if (size > 3) {
          filter = isRedStar
            ? 'drop-shadow(0 0 16px 8px #FF7A1888) drop-shadow(0 0 32px 16px #FF7A1844)'
            : 'drop-shadow(0 0 16px 8px #FFC51888) drop-shadow(0 0 32px 16px #FFC51844)';
        }
        // Twinkle animation: randomize duration and delay for each star
        const twinkleDuration = 2.5 + Math.random() * 2.5; // 2.5s to 5s
        const twinkleDelay = Math.random() * 5; // 0-5s
        stars.push(
          <g key={i} opacity={starOpacity} style={{ mixBlendMode: 'color-dodge' }}>
            <defs>
              {isRedStar ? (
                <radialGradient id={gradId} cx='0.5' cy='0.5' r='0.5' fx='0.5' fy='0.5'>
                  <stop offset='0%' stopColor='#FFF7A0' />
                  <stop offset='20%' stopColor='#FFB340' stopOpacity='0.95' />
                  <stop offset='50%' stopColor='#FF7A18' stopOpacity='0.7' />
                  <stop offset='80%' stopColor='#A82C0F' stopOpacity='0.3' />
                  <stop offset='100%' stopOpacity='0' />
                </radialGradient>
              ) : (
                <radialGradient id={gradId} cx='0.5' cy='0.5' r='0.5' fx='0.5' fy='0.5'>
                  <stop offset='0%' stopColor='#FFC518' />
                  <stop offset='4%' stopColor='#F0B916' stopOpacity='0.94' />
                  <stop offset='25%' stopColor='#A8820F' stopOpacity='0.66' />
                  <stop offset='45%' stopColor='#6C530A' stopOpacity='0.43' />
                  <stop offset='63%' stopColor='#3D2F05' stopOpacity='0.24' />
                  <stop offset='78%' stopColor='#1C1502' stopOpacity='0.11' />
                  <stop offset='91%' stopColor='#070500' stopOpacity='0.03' />
                  <stop offset='100%' stopOpacity='0' />
                </radialGradient>
              )}
            </defs>
            <circle
              cx={`${xPercent}%`}
              cy={`${yPercent}%`}
              r={size}
              fill={`url(#${gradId})`}
              className='star-twinkle'
              style={{
                filter,
                opacity: 1,
                transition: 'opacity 0.5s',
                animationDuration: `${twinkleDuration}s`,
                animationDelay: `${twinkleDelay}s`,
              }}
            />
          </g>,
        );
        i += 1;
      }
    }
    return stars;
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100vw',
        height: '100dvh',
        overflow: 'hidden',
        background: '#04121B',
      }}
    >
      {/* Star field (zIndex 1) */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <svg width='100%' height='100%' style={{ position: 'absolute', top: 0, left: 0 }}>
          {renderStars}
        </svg>
      </Box>

      {/* Lantern Container (zIndex 2) */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          zIndex: 2,
          maskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            // Each row holds lanternsPerRow lanterns (responsive)
            height: `${Math.ceil((thoughts.length + lanternsPerRow) / lanternsPerRow) * 180}px`,
            width: '100%',
          }}
        >
          {thoughts.map((thought, idx) => {
            const lantern = lanternState[thought];
            const state = lantern?.unlit ? 'unlit' : 'lit';
            const pos = lanternPositions[idx] || { left: '50%', top: '0px' };
            const floatAnim = floatAnimRef.current[idx] || { duration: 5, delay: 0 };
            // Use precomputed random attributes
            const attr = lanternAttributes[idx] || { color: 'yellow', size: 'md', rotationSeed: 1 };
            const floatKeyframes = keyframes`
              0% { transform: translateY(0) scale(1) rotate(${attr.rotationSeed * 5 * 2}deg); }
              20% { transform: translateY(-8px) scale(1.01) rotate(${attr.rotationSeed * 5 * 3}deg); }
              40% { transform: translateY(-16px) scale(1.02) rotate(${attr.rotationSeed * 5 * 4}deg); }
              60% { transform: translateY(-8px) scale(1.01) rotate(${attr.rotationSeed * 5 * 3}deg); }
              80% { transform: translateY(0) scale(1) rotate(${attr.rotationSeed * 5 * 2}deg); }
              100% { transform: translateY(0) scale(1) rotate(${attr.rotationSeed * 5 * 2}deg); }
            `;
            return (
              <LanternFloatDiv
                key={thought}
                customStyle={{
                  position: 'absolute',
                  ...pos,
                  transformOrigin: '50% 80%',
                  willChange: 'transform',
                  animation: `${floatKeyframes} ${floatAnim.duration}s ease-in-out 0s infinite`,
                }}
              >
                <Lantern
                  color={attr.color}
                  state={state}
                  size={attr.size}
                  onClick={() => {
                    trackMixpanelEvent(MixpanelEventName.SHAREYOURLIGHT_LANTERN_CLICKED, {
                      thoughtId: thought,
                      state,
                    });
                    if (state === 'lit') {
                      setLanternState((prev) => ({
                        ...prev,
                        [thought]: {
                          ...(prev[thought] || {}),
                          color: attr.color,
                          unlit: true,
                        },
                      }));
                      setSelected(thought);
                    }
                    else {
                      setSelected(thought);
                    }
                  }}
                />
              </LanternFloatDiv>
            );
          })}
          <SYLButton
            sx={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 'calc(max(35dvh, 200px))',
              maxWidth: 255,
              width: '100%',
              zIndex: 10,
            }}
            onClick={() => {
              trackMixpanelEvent(MixpanelEventName.SHAREYOURLIGHT_SHARE_YOUR_LIGHT_BUTTON_CLICKED);
              setAddMode(true);
            }}
            variant='outlined'
          >
            SHARE YOUR LIGHT
          </SYLButton>
        </Box>
      </Box>

      {/* Modals */}
      <ThoughtViewModal
        open={!!selected || !!openThoughtId}
        thoughtId={selected || openThoughtId}
        onClose={() => {
          setSelected('');
          setOpenThoughtId('');
          // Remove ?thoughtId from URL
          const url = new URL(window.location.href);
          url.searchParams.delete('thoughtId');
          window.history.replaceState({}, '', url.pathname);
        }}
        setAddMode={setAddMode}
        liked={likedLanterns[selected || openThoughtId] || false}
        setLiked={(val: boolean) => setLikedLanterns(
          (prev) => ({ ...prev, [selected || openThoughtId]: val }),
        )}
        csrfToken={csrfToken}
        refetchCsrfToken={refetchCsrfToken}
      />
    </Box>
  );
}

export default LanternSky;
